import { useState } from 'react';
import {
  IPIP_NEO_120_QUESTIONS,
  IPIP_NEO_50_QUESTIONS,
} from '../../content/ipipNeoContent';
import { IPIP_NEO_FACETS } from '../../content/personaContent';
import styles from './IpipNeoTestStep.module.css';

const options = [
  { value: 1, label: 'Very Inaccurate' },
  { value: 2, label: 'Moderately Inaccurate' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Moderately Accurate' },
  { value: 5, label: 'Very Accurate' },
];
const QUESTIONS_PER_PAGE = 10;

function IpipNeoTestStep({
  personaData,
  setPersonaData,
  onNext,
  onBack,
  currentStepNumber,
  totalSteps,
}) {
  const [answers, setAnswers] = useState({});
  const [selectedTest, setSelectedTest] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const questions =
    selectedTest === 'quick' ? IPIP_NEO_50_QUESTIONS : IPIP_NEO_120_QUESTIONS;

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentQuestions = questions.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE
  );

  const answeredOnThisPage = currentQuestions.filter(
    (q, index) =>
      answers[currentPage * QUESTIONS_PER_PAGE + index] !== undefined
  ).length;
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions.length;

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: value }));
  };

  const calculateScores = () => {
    const isQuick = selectedTest === 'quick';
    const finalScores = {};
    Object.keys(IPIP_NEO_FACETS).forEach((domain) => {
      finalScores[domain] = {};
      Object.keys(IPIP_NEO_FACETS[domain].facets).forEach((facet) => {
        finalScores[domain][facet] = 50;
      });
    });

    const domainScores = {
      openness: [],
      conscientiousness: [],
      extraversion: [],
      agreeableness: [],
      neuroticism: [],
    };

    questions.forEach((q, index) => {
      let value = answers[index];
      if (value === undefined) value = 3;
      if (q.reversed) value = 6 - value;

      if (isQuick) {
        domainScores[q.domain].push(value);
      } else {
        finalScores[q.domain][q.facet] =
          (finalScores[q.domain][q.facet] || 0) + value;
      }
    });

    // Normalize scores
    Object.keys(IPIP_NEO_FACETS).forEach((domain) => {
      Object.keys(IPIP_NEO_FACETS[domain].facets).forEach((facet) => {
        if (!isQuick) {
          // Full test has 4 questions per facet
          const count = 4;
          const minScore = count * 1;
          const maxScore = count * 5;
          const score = finalScores[domain][facet];
          const normalized = Math.round(
            ((score - minScore) / (maxScore - minScore)) * 100
          );
          finalScores[domain][facet] = Math.max(1, Math.min(100, normalized));
        }
      });
      if (isQuick) {
        // Quick test has 10 questions per domain, so 50 is max, 10 is min
        const domainScoreSum = domainScores[domain].reduce((a, b) => a + b, 0);
        const normalizedDomain = Math.round(((domainScoreSum - 10) / 40) * 100);
        Object.keys(finalScores[domain]).forEach((facet) => {
          finalScores[domain][facet] = Math.max(
            1,
            Math.min(100, normalizedDomain)
          );
        });
      }
    });

    return finalScores;
  };

  const handleNext = () => {
    const personalityScores = calculateScores();
    setPersonaData((prevData) => ({
      ...prevData,
      personalityData: personalityScores,
    }));
    onNext();
  };

  if (!selectedTest) {
    return (
      <div>
        <h3>
          Step {currentStepNumber}/{totalSteps}: Personality Assessment
        </h3>
        <p>
          Choose your assessment method. A deeper analysis provides more nuanced
          results but takes longer.
        </p>
        <div className={styles.testChooser}>
          <div
            className={styles.choiceCard}
            onClick={() => setSelectedTest('quick')}
          >
            <h4>🚀 Quick Fit</h4>
            <p>
              50 questions to get a great baseline of the 5 major personality
              domains.
            </p>
          </div>
          <div
            className={styles.choiceCard}
            onClick={() => setSelectedTest('full')}
          >
            <h4>🔬 Full Depth</h4>
            <p>
              120 questions for a detailed, 30-facet analysis of the persona's
              character.
            </p>
          </div>
          <div className={styles.choiceCard} onClick={onNext}>
            <h4>🎛️ Manual Sliders</h4>
            <p>
              Skip the test and fine-tune all 30 personality facets yourself.
            </p>
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <button onClick={onBack} className="action-button secondary">
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3>
        Step {currentStepNumber}/{totalSteps}:{' '}
        {selectedTest === 'quick' ? 'Quick Fit (50 Qs)' : 'Full Depth (120 Qs)'}
      </h3>

      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
          />
        </div>
        <span className={styles.progressText}>
          Page {currentPage + 1} of {totalPages}
        </span>
      </div>

      <div className={styles.questionList}>
        {currentQuestions.map((q, index) => {
          const questionIndex = currentPage * QUESTIONS_PER_PAGE + index;
          return (
            <div key={questionIndex} className={styles.questionItem}>
              <p className={styles.questionText}>
                {questionIndex + 1}. {q.text}
              </p>
              <div className={styles.radioGrid}>
                {options.map((opt) => (
                  <label key={opt.value} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name={`question-${index}`}
                      value={opt.value}
                      checked={answers[questionIndex] === opt.value}
                      onChange={() =>
                        handleAnswerChange(questionIndex, opt.value)
                      }
                      className={styles.radioInput}
                    />
                    <span className={styles.radioControl}></span>
                    <span className={styles.radioText}>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.buttonGroup}>
        <button
          onClick={() =>
            currentPage > 0
              ? setCurrentPage(currentPage - 1)
              : setSelectedTest(null)
          }
          className="action-button secondary"
        >
          {currentPage > 0 ? 'Previous' : 'Back'}
        </button>
        {currentPage < totalPages - 1 ? (
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="action-button primary"
            disabled={answeredOnThisPage < QUESTIONS_PER_PAGE}
            title={
              answeredOnThisPage < QUESTIONS_PER_PAGE
                ? 'Please answer all questions on this page'
                : 'Next Page'
            }
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="action-button primary"
            disabled={answeredCount < totalQuestions}
            title={
              answeredCount < totalQuestions
                ? 'Please answer all questions'
                : 'Finish & Calculate'
            }
          >
            Finish & Calculate
          </button>
        )}
      </div>
    </div>
  );
}

export default IpipNeoTestStep;
